import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw, ClipboardCopy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const RELOAD_TS_KEY = '__chunk_reload_ts';

/**
 * Detecte les erreurs de chargement de chunk JS (lazy load qui
 * echoue parce que le bundle reference n'existe plus apres un
 * deploy). Couvre :
 *   - Vite/webpack : message contient 'Failed to fetch dynamically
 *     imported module' ou 'Loading chunk N failed'
 *   - Safari iOS : "'text/html' is not a valid JavaScript MIME type"
 *     (Netlify retourne /index.html via SPA fallback pour les .js
 *     introuvables)
 *   - Generic ChunkLoadError name
 */
function isChunkLoadError(error: Error): boolean {
  const msg = (error.message || '').toLowerCase();
  return (
    error.name === 'ChunkLoadError' ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes("'text/html' is not a valid javascript mime type") ||
    msg.includes('expected a javascript module script') ||
    msg.includes('importing a module script failed')
  );
}

/**
 * Cas particulier d'un chunk périmé : un composant chargé via lazy() se
 * résout en `undefined` (le fichier JS référencé n'existe plus après un
 * deploy → Netlify renvoie index.html, sans export `default`). React lève
 * alors « Cannot read properties of undefined (reading 'default') » lors du
 * rendu du <Lazy>. On ne le traite comme chunk périmé QUE si l'erreur
 * survient bien sous un composant Lazy (pour ne pas confondre avec un vrai
 * bug « reading default » ailleurs).
 */
function isLazyDefaultError(
  error: Error,
  componentStack: string | null
): boolean {
  const msg = (error.message || '').toLowerCase();
  const readingDefault =
    msg.includes("reading 'default'") || msg.includes('reading "default"');
  return readingDefault && !!componentStack && /\blazy\b/i.test(componentStack);
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  copied: boolean;
}

/**
 * Attrape les erreurs JS qui plantent un composant React et affiche
 * un écran de récupération au lieu du fond noir vide.
 *
 * Sans cette boundary, n'importe quelle exception non gérée fait
 * démonter tout l'arbre React → l'utilisateur voit juste le bg du
 * body (#0A0A0A) sans aucune info ni moyen de revenir.
 *
 * Le panneau Détails techniques affiche l'URL courante + bouton
 * "Copier rapport" qui copie un JSON débogable dans le presse-papiers
 * (plus utile qu'une capture d'écran à envoyer à Marc-André).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    componentStack: null,
    copied: false,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    // Log pour qu'on retrouve l'erreur dans la console du navigateur
    console.error('🚨 ErrorBoundary a attrapé une erreur :', error);
    console.error('Component stack :', errorInfo.componentStack);

    // Auto-reload quand un chunk JS référencé n'existe plus (cache stale
    // après un deploy) : ChunkLoadError, MIME error Safari, OU un lazy()
    // qui se résout en undefined (« reading 'default' » sous un <Lazy>).
    // Anti-boucle par horodatage : si on a déjà rechargé il y a moins de
    // 12s, c'est que le bundle est vraiment cassé → on montre l'erreur.
    const staleChunk =
      isChunkLoadError(error) ||
      isLazyDefaultError(error, errorInfo.componentStack ?? null);
    if (staleChunk) {
      try {
        const now = Date.now();
        const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || '0');
        if (now - last > 12000) {
          sessionStorage.setItem(RELOAD_TS_KEY, String(now));
          console.warn(
            'Chunk JS périmé (cache stale après deploy) — reload automatique.'
          );
          window.location.reload();
        } else {
          console.warn(
            'Chunk encore introuvable après reload — affichage de l\'erreur.'
          );
        }
      } catch {
        // sessionStorage indisponible (mode privé Safari) — laisse
        // l'erreur s'afficher normalement
      }
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
      copied: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleCopyReport = async () => {
    const report = {
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      message: this.state.error?.message ?? 'Erreur inconnue',
      stack: this.state.error?.stack ?? null,
      componentStack: this.state.componentStack ?? null,
      timestamp: new Date().toISOString(),
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      this.setState({ copied: true });
      // reset le feedback après 2.5s
      setTimeout(() => this.setState({ copied: false }), 2500);
    } catch {
      // Pas de clipboard API : fallback prompt() pour copie manuelle
      window.prompt('Copie ce rapport :', JSON.stringify(report));
    }
  };

  render() {
    if (this.state.hasError) {
      const errorMsg = this.state.error?.message ?? 'Erreur inconnue';
      const stack = this.state.componentStack ?? '';
      const url =
        typeof window !== 'undefined' ? window.location.href : 'unknown';

      return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#E8E0D6] flex items-center justify-center p-6">
          <div className="max-w-lg w-full rounded-2xl border border-red-500/40 bg-red-500/5 p-8">
            <div className="text-center mb-6">
              <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl text-red-300 mb-2">
                Oups — quelque chose a planté
              </h1>
              <p className="text-sm text-neutral-400">
                Une erreur s'est produite. Essaie de recharger la page. Si ça
                continue, clique sur «&nbsp;Copier le rapport&nbsp;» et
                envoie-le à Marc-André.
              </p>
            </div>

            <details className="rounded-md bg-[#0A0A0A] border border-red-500/20 p-3 mb-6">
              <summary className="text-xs text-neutral-500 cursor-pointer">
                Détails techniques (utile pour debug)
              </summary>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-600">
                    URL
                  </div>
                  <p className="text-xs text-neutral-300 font-mono break-all">
                    {url}
                  </p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-600">
                    Message
                  </div>
                  <p className="text-xs text-red-300 font-mono break-words">
                    {errorMsg}
                  </p>
                </div>
                {stack && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-600">
                      Stack composants
                    </div>
                    <pre className="text-[10px] text-neutral-500 mt-1 overflow-x-auto max-h-40 whitespace-pre-wrap">
                      {stack.trim()}
                    </pre>
                  </div>
                )}
              </div>
            </details>

            <div className="flex gap-2 justify-center flex-wrap">
              <Button
                onClick={this.handleReload}
                className="bg-[#D4A574] text-[#0A0A0A] hover:bg-[#C9A36B] font-medium"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Recharger la page
              </Button>
              <Button
                onClick={this.handleCopyReport}
                variant="outline"
                className="border-[#D4A574]/40 bg-transparent text-[#D4A574] hover:bg-[#D4A574]/10"
              >
                {this.state.copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copié !
                  </>
                ) : (
                  <>
                    <ClipboardCopy className="h-4 w-4 mr-2" />
                    Copier le rapport
                  </>
                )}
              </Button>
              <Button
                onClick={this.handleReset}
                variant="outline"
                className="border-[#2A2A2A] bg-transparent text-neutral-400 hover:bg-[#1A1A1A] hover:text-[#E8E0D6]"
              >
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
